/**
 * Simul guided-tour runtime adapter.
 *
 * Why a separate module?
 *  1. The Simul runtime is a non-trivial bundle (~tens of KB plus
 *     `@floating-ui/dom`). Static-importing it from main.tsx pushes
 *     that into every page load even for users who will never see a
 *     tour — keeping it dynamic-import-only lets the production
 *     bundler tree-shake it out of the critical chunk.
 *  2. The gate (query string + Vite env var) lives in one place so
 *     the UserMenu's "Start guided tour" links and main.tsx
 *     bootstrap don't drift apart.
 *
 * Activation rules:
 *  - `?simul=true` on any URL turns Simul ON for the rest of the
 *    session (cached in sessionStorage so route changes don't lose it).
 *  - `VITE_SIMUL_ENABLED=true` at build time turns it on globally
 *    (preview / staging builds).
 *  - Otherwise OFF. The UserMenu "Start tour" link will short-circuit
 *    with a console.warn rather than silently swallowing the click.
 */

const SESSION_FLAG_KEY = 'kairos.simul.enabled';

export function isSimulEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  // Vite env var — baked at build time. Cast through `unknown` so we
  // don't pull in vite/client types at the lib level (the host app
  // already has them).
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  if (env?.VITE_SIMUL_ENABLED === 'true') return true;

  // Query string — checked once per page load. Sticky for the session
  // so route changes don't disable Simul mid-flow.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('simul') === 'true') {
      window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
      return true;
    }
    if (window.sessionStorage.getItem(SESSION_FLAG_KEY) === '1') return true;
  } catch {
    // sessionStorage can throw in privacy modes — treat as "off".
  }
  return false;
}

/**
 * Lazily import the Simul runtime + start a tour. Returns the
 * controller so callers can stop/advance it programmatically, or
 * `null` if Simul is gated off (the gate has already logged a warning).
 *
 * `import('@lopettia/simul')` is a *dynamic* import on purpose — Vite
 * splits the runtime into its own chunk and only fetches it the first
 * time a tour actually starts. Production builds without the gate
 * therefore pay zero kB for Simul.
 */
export async function startTourLazy(
  tour: import('@lopettia/simul').SimulTour,
): Promise<import('@lopettia/simul').TourController | null> {
  if (!isSimulEnabled()) {
    // Soft fail — the UserMenu can still surface the link, but
    // clicking it without the gate is a no-op that the developer
    // console will explain. Production builds without the env flag
    // simply won't reach this branch because the menu hides the link
    // (see UserMenu.tsx).
    console.warn(
      '[simul] Tour requested but Simul is gated off. '
        + 'Add ?simul=true to the URL or set VITE_SIMUL_ENABLED=true.',
    );
    return null;
  }
  const { startTour } = await import('@lopettia/simul');
  const controller = startTour(tour);
  controller.start();
  return controller;
}
